/**
 * OSNAP-CAPABILITY-MAP — the single, authoritative table of which OSNAP
 * modes each *renderable* geometry kind can emit.
 *
 * WHY this lives in `scene3d/snap` and not `command-engine` / a future
 * `cad-core`: the table MUST key on the renderable `SceneGeometry["kind"]`
 * (a scene3d type), NOT on the contracts `EntityType`. They are not 1:1 —
 * e.g. a Revision Cloud has `EntityType === "Revision Cloud"` but its
 * renderable geometry kind is `"polyline"`, and the candidate generator
 * (`osnapCandidates.ts`) dispatches on `geometry.kind`. Keying on
 * `EntityType` would therefore change behaviour. `command-engine` /
 * `cad-core` cannot import the scene3d kind union anyway (forbidden
 * reverse edge / dependency cycle per `.dependency-cruiser.cjs`), so the
 * typed map is co-located here with `OsnapMode` and the generator.
 *
 * CAD-core promotion is a separate, future Category-C redesign (the kind
 * union would have to move first). Do NOT attempt it from this module.
 *
 * The `Record<RenderableKind, …>` type gives compile-time exhaustiveness:
 * adding a new `SceneGeometry["kind"]` without a matching entry here is a
 * `tsc` error (TS2741), so the table can never silently drift behind the
 * geometry union.
 *
 * Conventions mirror `packages/command-engine/src/propertyCapabilities.ts`
 * (exhaustive `Record`, `as const` arrays, thin accessors). No DOM / THREE
 * imports — pure data + lookups, fully unit-testable.
 */

import type { SceneGeometry } from "../types";
import type { OsnapMode } from "./osnapTypes";

/** The renderable geometry kind discriminant — the map's key set. */
export type RenderableKind = SceneGeometry["kind"];

/**
 * Per-kind OSNAP capability set. Each array is the EXACT set of modes the
 * corresponding handler in `osnapCandidates.ts` can emit (verified
 * byte-exact against the handlers). Order is documentary only — callers
 * treat each as a set (`includes` / `Set`).
 *
 * The six compound kinds (hatch / wipeout / viewport / array-rect /
 * dimension / multileader) emit NO candidates: their child primitives are
 * surfaced separately by the spatial index, so an empty `[]` is the single
 * source of truth for "this kind snaps to nothing".
 */
/**
 * OSNAP-SNAP-EXPAND — EXTENSION (bit 4096) / PARALLEL (bit 8192) coverage.
 * AutoCAD's Object Snap Tab reference documents EXTENSION as a temporary
 * "extension line OR ARC" past the endpoint of an object — line, polyline
 * (open ends), arc, elliptical arc, and (endpoint tangent-line) spline. A
 * CLOSED curve has no open end, so `circle` stays without it, and `ellipse`
 * only emits it for a genuine partial sweep (see `handleEllipse`). PARALLEL,
 * by the SAME reference, "constrains a new line segment, polyline segment,
 * ray or xline to be parallel to an existing LINEAR object" — so only `line`
 * and `polyline` (each straight segment) carry it; `arc` / `ellipse` /
 * `spline` are curves with no single reference direction and are
 * intentionally excluded (a documented CAD-parity decision, NOT a
 * scope-narrowing omission). Both modes stay the documented degenerate
 * placeholder (`OSNAP_MODES_PENDING_IMPLEMENTATION` in resolveObjectSnap.ts —
 * no reference-direction gate on PARALLEL yet); this slice only widens WHICH
 * kinds emit a candidate, not that semantic gap.
 */
export const osnapCapabilityMap: Record<RenderableKind, readonly OsnapMode[]> = {
  line: ["endpoint", "midpoint", "perpendicular", "nearest", "extension", "parallel"],
  polyline: [
    "endpoint",
    "midpoint",
    "nearest",
    "perpendicular",
    "geomcenter",
    "extension",
    "parallel",
  ],
  circle: ["center", "geomcenter", "quadrant", "nearest", "perpendicular", "tangent"],
  arc: [
    "center",
    "geomcenter",
    "endpoint",
    "midpoint",
    "quadrant",
    "nearest",
    "perpendicular",
    "tangent",
    "extension",
  ],
  ellipse: ["center", "geomcenter", "quadrant", "nearest", "extension"],
  spline: ["endpoint", "nearest", "extension"],
  // A GENUINE DXF POINT. `kind: "point"` is also the shape of a cache-direct
  // METADATA STUB (`state/spatialBuilder.ts metadataToSpatialStub`) standing in
  // for an entity of any type the renderer never lifted; such a record carries
  // no geometry and emits NOTHING (`osnapCandidates.ts` `case "point"` →
  // `isMetadataStubRenderable`). That is an ENTITY-level gate, not a kind-level
  // one, so this entry stays the point handler's exact mode set.
  point: ["node"],
  "block-insert": ["insertion"],
  // A heavy POLYLINE MESH's wireframe. Its runs ARE polylines (rows / columns
  // of a polygon mesh, visible face edges of a poly-face mesh), so it snaps
  // exactly like one — endpoints, edge midpoints, nearest / perpendicular on an
  // edge, the wire hull's geometric centre, and EXTENSION past an open run's
  // free end. A mesh has NO insertion point (it is not a block reference): the
  // former `block-insert` wrapper advertised one, fabricated from the POLYLINE's
  // group 10/20/30 "dummy point" whose X/Y the DXF reference fixes at 0, so an
  // INSert snap could appear at the world origin while every real edge was
  // unsnappable (@codex PR #785 round 3, F4).
  "mesh-wire": [
    "endpoint",
    "midpoint",
    "nearest",
    "perpendicular",
    "geomcenter",
    "extension",
    "parallel",
  ],
  text: ["insertion"],
  mtext: ["insertion"],
  hatch: [],
  wipeout: [],
  viewport: [],
  "array-rect": [],
  dimension: [],
  multileader: [],
} as const;

/** The OSNAP modes a given renderable kind can emit (empty if none). */
export function getOsnapModesForKind(kind: RenderableKind): readonly OsnapMode[] {
  return osnapCapabilityMap[kind];
}

/** True iff `mode` is in `kind`'s capability set. */
export function canSnapKind(kind: RenderableKind, mode: OsnapMode): boolean {
  return osnapCapabilityMap[kind].includes(mode);
}

/**
 * Which renderable kinds can be an OPERAND of an Extended (Apparent)
 * Intersection override (ТЗ §8.6, rule §546 — a snap capability map that
 * explicitly fixes the applicable / inapplicable kinds). This is a SEPARATE
 * capability surface from the running-snap {@link osnapCapabilityMap}: the two
 * override modes carry no OSMODE bit and are never emitted by the per-entity
 * generators, so they must not sit in the running-snap map (whose exhaustiveness
 * tests pin `emitted === map` over the per-entity handlers).
 *
 * An extended crossing is computed from a kind's canonical straight/conic
 * decomposition (`osnapIntersections.streamShapesForEntity`), so the operand set
 * is exactly the kinds that yield ≥1 straight/conic shape this slice: LINE,
 * LWPOLYLINE, CIRCLE, ARC. ELLIPSE / SPLINE (and every compound / annotation
 * kind) yield no straight-or-conic shape yet → `false` (the documented
 * OSNAP-INTERSECT-EMIT-S2 follow-up, shared with the running INTERSECTION pass).
 * The `Record<RenderableKind, …>` type keeps it exhaustive over the geometry
 * union (a new kind without an entry is a `tsc` error).
 */
export const extendedIntersectionOperandMap: Record<RenderableKind, boolean> = {
  line: true,
  polyline: true,
  circle: true,
  arc: true,
  ellipse: false,
  spline: false,
  point: false,
  "block-insert": false,
  // Straight mesh edges ARE intersectable — `streamShapesForEntity` yields one
  // `seg` per drawn edge, the same shape a `polyline` yields per span.
  "mesh-wire": true,
  text: false,
  mtext: false,
  hatch: false,
  wipeout: false,
  viewport: false,
  "array-rect": false,
  dimension: false,
  multileader: false,
};

/**
 * True iff `kind` can be an operand of an Extended (Apparent) Intersection
 * override. Thin accessor over {@link extendedIntersectionOperandMap}.
 */
export function canParticipateInExtendedIntersection(
  kind: RenderableKind,
): boolean {
  return extendedIntersectionOperandMap[kind];
}
